import { useAppDispatch } from 'hooks/redux';
import React from 'react';

import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';

import InfoIcon from '@mui/icons-material/InfoRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import QIllustration from 'components/QIllustration';
import type { IllustrationName } from 'components/QIllustration/QIllustrationConfig';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textAlign: 'center',
    flexDirection: 'column',
    padding: '16px 0',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 544,
    gap: 16,
  },
  info: {
    margin: 8,
    marginBottom: 32,
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: 8,
  },
  infoIcon: {
    marginRight: 8,
    color: theme.palette.info.main,
  },
}));

interface HoldingScreenProps {
  name: IllustrationName;
  imgAlt?: string;
  title?: string;
  subtitle?: string;
  info?: string;
  button?: any;
  width?: number;
  height?: number;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

const HoldingScreen: React.FC<HoldingScreenProps> = ({
  name,
  title,
  subtitle,
  info,
  button,
  width = 128,
  height = 128,
}) => {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.root}>
        <div style={{ padding: '20px' }}>
          <QIllustration name={name} width={width} height={height} />
        </div>
        <div className={classes.column}>
          <Typography variant='headlineRegular'>{title}</Typography>
          <Typography variant='bodyRegular'>{subtitle}</Typography>
          {info ? (
            <Typography variant='paragraphRegular' className={classes.info}>
              <InfoIcon fontSize='small' className={classes.infoIcon} />
              {info}
            </Typography>
          ) : null}
          {button}
        </div>
      </div>
    </>
  );
};

export default HoldingScreen;

// export const CompleteView = () => (
//   <HoldingScreen imgSrc={HappyStar} imgAlt='Happy Star' title='Woo-hoo!' subtitle='Your bills are now connected.' />
// );

export const PendingView = () => (
  <HoldingScreen
    name='billPresentmentTime'
    imgAlt='Bill-Linked'
    title='All set!'
    subtitle='We’re verifying your account details and will let you know once your biller is connected.'
    info='It may take 1-2 days for your biller to connect.'
  />
);

export const IntroView = () => (
  <HoldingScreen
    name='billConnectStep'
    imgAlt='Lightbulb-Calendar'
    width={312}
    height={168}
    title='Connecting your bills'
    subtitle='Find your biller and enter your account details to connect. Simplifi will update your Reminders with your latest bill details for accurate cash flow projection!'
  />
);

export const InitView = (model: ScheduledTransactionType) => {
  const dispatch = useAppDispatch();

  return (
    <HoldingScreen
      name='billConnect'
      imgAlt='Bill-Link'
      title='View your bills in Simplifi!'
      subtitle='Connect your biller to automatically update Reminders with your latest bill details—great for cash flow projection!'
      button={
        <Button variant='contained' id='BPS-connect' onClick={() => dispatch(doBillPresentment(model, 'edit series'))}>
          <LinkIcon style={{ marginRight: 4 }} /> Connect biller
        </Button>
      }
    />
  );
};
